import { AUDITLOG_SRC, AuditLogRecord } from 'core/auditLog/AuditLog';
import { AbstractAuditLogModel } from './AuditLogModel';
import { AuditLogManager, DefaultAuditLogManager } from 'core/auditLog/AuditLogManager';
import { ColumnDescription, ExpandRowProps } from 'react-bootstrap-table-next';
import formatters from './AuditLogFormatters';
import { renderColumn } from 'components/TableColumn/TableColumn';
import { SelectOptions } from 'components/commonType';
import { toast } from 'react-toastify';
import _ from 'lodash';
import i18n from 'i18n';

export enum ADVERTISER_AUDITLOG_COLUMN {
  DATE = 'ctime',
  SOURCE_INFO = 'srcID',
  EMAIL = 'email',
  EVENT = 'funcType',
  DETAIL = 'msg'
}

export type AdvertiserAuditLogListRow = {
  id: number,
  [ADVERTISER_AUDITLOG_COLUMN.DETAIL]: { [key: string]: {
    adminOnly?: boolean,
    org: string,
    to: string
  } }[],
  [ADVERTISER_AUDITLOG_COLUMN.DATE]: string;
  [ADVERTISER_AUDITLOG_COLUMN.SOURCE_INFO]: {
    id: number;
    type: AUDITLOG_SRC;
    name: string
  },
  [ADVERTISER_AUDITLOG_COLUMN.EMAIL]: string;
  [ADVERTISER_AUDITLOG_COLUMN.EVENT]: string;
};

export class AdvertiserAuditLogModel extends AbstractAuditLogModel<AdvertiserAuditLogListRow> {

  nonExpandRowKey: number[] = [];

  constructor (
    private isAdmin: boolean,
    public advertiserId: string | number,
    auditLogManager: AuditLogManager = new DefaultAuditLogManager()
  ) {
    super(auditLogManager);
  }

  getSearchString () {
    let searchString = super.getSearchString();
    if (!_.isEmpty(searchString)) {
      return `${searchString}`;
    }
    return '';
  }

  getIgnoreFields () {
    return [
      'id', // use advertiserId instead
      'category',
      'website',
      'note',
      'preferredPublisherId'
    ];
  }

  override getFilters (): {[key: string]: {
    options: SelectOptions[],
    value?: string | number,
    onSelect: (campaignTarget: string | number) => void;
  }} {
    const filters = {};
    let generalOptions: SelectOptions[] = [];
    this.auditLogFields
      .filter(field => !this.getIgnoreFields().includes(field))
      .forEach(field => {
        const label = i18n.t<string>(`auditLog.labels.${field}`);
        label && generalOptions.push({
          label: label,
          value: field
        });
      });
    const auditLogFieldOptions = generalOptions;
    auditLogFieldOptions.unshift({
      label: i18n.t<string>('common.placeholder.all'),
      value: ''
    });
    filters['fieldFilter'] = {
      options: auditLogFieldOptions,
      value: this.selectedAuditLogField,
      onSelect: this.setAuditLogField
    };
    return filters;
  }

  getAuditLogDetail = (messages) => {
    return messages && Array.isArray(messages) ?
      messages.filter(msg => {
        if (_.some(Object.keys(msg), key => this.getIgnoreFields().includes(key))) {
          return false;
        }
        const values: any = _.defaultTo(Object.values(msg), []);
        if (values.length === 0) return true;
        const content = values[0];
        return content.adminOnly ? content.adminOnly && this.isAdmin : true;
      }) :
      messages;
  }

  getAuditLogDataOfRecord = (record: AuditLogRecord) => {
    if (!record.msg || record.msg.length === 0) {
      this.nonExpandRowKey.push(record.id);
    }
    return {
      id: record.id,
      [ADVERTISER_AUDITLOG_COLUMN.DETAIL]: this.getAuditLogDetail(record.msg),
      [ADVERTISER_AUDITLOG_COLUMN.DATE]: record.ctime,
      [ADVERTISER_AUDITLOG_COLUMN.SOURCE_INFO]: {
        id: record.srcID,
        type: record.srcType,
        name: ''
      },
      [ADVERTISER_AUDITLOG_COLUMN.EMAIL]: record.email,
      [ADVERTISER_AUDITLOG_COLUMN.EVENT]: record.funcType
    };
  }

  async initAuditLog (page: number = 1) {
    try {
      const pageable = { page: page, sizePerPage: 10, sort: this.sortField === ADVERTISER_AUDITLOG_COLUMN.EMAIL ? 'userId' : this.sortField , direction: this.sortOrder };
      const auditLogFromServer = await this.auditLogManager.getAdvertiserAuditLog(this.advertiserId, pageable, this.getSearchString());
      this.pagination = auditLogFromServer.pagination;
      this.nonExpandRowKey = [];
      this.auditLogData = auditLogFromServer.records.map(this.getAuditLogDataOfRecord);
      this.updateFilteredAuditLogData();
    } catch (e) {
      (e instanceof Error) && toast.error(e.message);
      this.auditLogData = [];
    }
  }

  async getAuditLogFields () {
    try {
      this.auditLogFields = await this.auditLogManager.getAdvertiserAuditLogFields(this.advertiserId);
      this.auditLogFields.sort();
    } catch (e) {
      (e instanceof Error) && toast.error(e.message);
    }
  }

  updateFilteredAuditLogData () {
    this.filteredAuditLogData = this.auditLogData ? this.auditLogData : [];
    // TODO: implement filter feature later
    return this.filteredAuditLogData;
  }

  getColumnFormatter (columnName) {
    switch (columnName) {
      case ADVERTISER_AUDITLOG_COLUMN.SOURCE_INFO:
        return formatters.srcInfoFormatter;
      case ADVERTISER_AUDITLOG_COLUMN.EVENT:
        return formatters.i18nFormatter;
      default:
        return;
    }
  }

  getSubFilters (): {
    [key: string]: {
      options: SelectOptions[],
      value?: string | number,
      onSelect: (target: string | number) => void;
    }} {
    return {
    };
  }

  getColumns (): ColumnDescription<AdvertiserAuditLogListRow>[] {
    const definitions = Object.values(ADVERTISER_AUDITLOG_COLUMN).map(columnToRender => {
      if (columnToRender === ADVERTISER_AUDITLOG_COLUMN.DETAIL) {
        return undefined;
      }
      return renderColumn(this.getAuditLogColumnDefinition(columnToRender), this.getColumnFormatter(columnToRender));
    });
    return _.compact(definitions);
  }

  getExpandRenderer (): ExpandRowProps<AdvertiserAuditLogListRow, number> {
    return {
      nonExpandable: this.nonExpandRowKey,
      renderer: formatters.detailExpandRenderer
    };
  }
}
